import React, { useContext, useState, useEffect } from "react";
import styles from "../dashboard/dashboard.module.scss";
import { connect } from "react-redux";
import { GanttChart } from "./component/GanttChart";
import {
  ArrowDownMedium,
  DayIcon,
  MonthIcon,
  QuarterIcon,
  ScopeViewIcon,
  WeekIcon,
  YearIcon,
} from "../../modules/default-svg";
import CustomButton from "../../components/controls/button/Button";
import CustomPopover from "../../components/popover/Popover";
import { SharedTimelineContext } from "./SharedTimelineContext";
import { Calendar, CalendarDays } from "lucide-react";
import KeyEventsPopover from "./components/KeyEventsPopover";
import keyEventsService from "../../store/services/keyEvents.service";
import { useDispatch } from "react-redux";
import { generalActions } from "../../store/actions";
import ReportButton from "../../components/Export/ReportButton";
import ExportButton from "../../components/Export/ExportButton";

const Timeline = ({ entityID, entityType, isFullHeight, setFullHeight }) => {
  const {
    setEntityID,
    setEntityType,
    tabView,
    setTabView,
    displayYearRange,
    setTodayTrigger,
    timelineData,
    timelineKeyEvents,
    fetchAndSetTimelineData,
  } = useContext(SharedTimelineContext);

  const dispatch = useDispatch();
  const [isHideSideBar, setIsHideSideBar] = useState(false);
  const [selection, setSelection] = useState({
    icon: <MonthIcon />,
    name: "Month",
  });
  const [openPopover, setIsOpenPopover] = useState(null);
  const [count, setCount] = useState(0);
  const [keyEventsOpen, setKeyEventsOpen] = useState(false);
  const [keyEvents, setKeyEvents] = useState([]);
  const [keyEventsLoading, setKeyEventsLoading] = useState(false);

  // Check permissions
  const permissions = localStorage.getItem("permissions");
  const canEditPortfolio = permissions && permissions.includes("Portfolio.Edit");

  useEffect(() => {
    if (entityID) {
      setEntityID(entityID);
      setEntityType(entityType);
    }
  }, [entityID, entityType]);

  useEffect(() => {
    if (timelineData && timelineData.length > 0 && entityType === "portfolio") {
      setKeyEvents(timelineKeyEvents);
    }
  }, [timelineData, entityType]);

  useEffect(() => {
    switch (tabView) {
      case "week":
        setSelection({ icon: <WeekIcon />, name: "Week" });
        break;
      case "day":
        setSelection({ icon: <DayIcon />, name: "Day" });
        break;
      case "quarter":
        setSelection({ icon: <QuarterIcon />, name: "Quarter" });
        break;
      case "year":
        setSelection({ icon: <YearIcon />, name: "Year" });
        break;
      default:
        setSelection({ icon: <MonthIcon />, name: "Month" });
        break;
    }
  }, [tabView]);

  const handleCreateKeyEvent = async (payload) => {
    try {
      setKeyEventsLoading(true);
      await keyEventsService.createKeyEvent(entityID, payload);
      dispatch(
        generalActions.showToast({
          title: "Key event created successfully",
          type: "success",
        })
      );
      await fetchAndSetTimelineData();
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: error?.response?.data?.message || "Failed to create key event",
          type: "error",
        })
      );
    } finally {
      setKeyEventsLoading(false);
    }
  };

  const handleUpdateKeyEvent = async (eventId, payload) => {
    try {
      setKeyEventsLoading(true);
      await keyEventsService.updateKeyEvent(entityID, eventId, payload);
      dispatch(
        generalActions.showToast({
          title: "Key event updated successfully",
          type: "success",
        })
      );
      await fetchAndSetTimelineData();
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: error?.response?.data?.message || "Failed to update key event",
          type: "error",
        })
      );
    } finally {
      setKeyEventsLoading(false);
    }
  };

  const handleDeleteKeyEvent = async (eventId) => {
    try {
      setKeyEventsLoading(true);
      await keyEventsService.deleteKeyEvent(entityID, eventId);
      dispatch(
        generalActions.showToast({
          title: "Key event deleted successfully",
          type: "success",
        })
      );
      await fetchAndSetTimelineData();
    } catch (error) {
      dispatch(
        generalActions.showToast({
          title: error?.response?.data?.message || "Failed to delete key event",
          type: "error",
        })
      );
    } finally {
      setKeyEventsLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-2 h-full" style={{ width: '100%' }}>
      <div
        className={styles.container}
        style={{ height: `calc(100vh - ${isFullHeight ? "190px" : "368px"})` }}
      >
        <div className={styles.secondHeader}>
          <div className={styles.content}>
            <CustomButton
              btnClass=""
              icon={<ScopeViewIcon isHide={isHideSideBar} />}
              clickHandler={() => {
                setIsHideSideBar(!isHideSideBar);
              }}
            />
          </div>
          <div className={styles.secondaryContent}>

            <div className={styles.featuresContainer}>
              <ReportButton
                parentType={entityType === "portfolio" ? "Portfolio" : "Program"}
                parentId={entityID}
              />
              <ExportButton config={{ parent: entityType === "portfolio" ? 1 : 2, parentId: entityID, category: 5 }} />
            </div>
            {/* Key Events Button - Only for Portfolio */}
            {entityType === "portfolio" && canEditPortfolio && (
              <KeyEventsPopover
                open={keyEventsOpen}
                onOpenChange={setKeyEventsOpen}
                timelineData={timelineData}
                keyEvents={keyEvents}
                onCreateEvent={handleCreateKeyEvent}
                onUpdateEvent={handleUpdateKeyEvent}
                onDeleteEvent={handleDeleteKeyEvent}
                isLoading={keyEventsLoading}
                portfolioId={entityID}
              >
                <div
                  className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer transition-all"
                  style={{
                    border: '1px solid #342c82',
                    borderRadius: '20px',
                    backgroundColor: keyEventsOpen ? '#ece7fd' : 'transparent',
                    color: '#342c82',
                    fontWeight: 500,
                    fontFamily: 'Figtree, sans-serif',
                    fontSize: '13px',
                    padding: '6px 12px',
                  }}
                >
                  <Calendar size={16} />
                  <span>Key Events</span>
                </div>
              </KeyEventsPopover>
            )}
            <CustomButton
              btnClass="todayBtn"
              label="Today"
              name="today"
              id="todayBtn"
              clickHandler={() => {
                setTodayTrigger(false);
                setTimeout(() => {
                  setTodayTrigger(true);
                }, 0);
              }}
            />
            <CustomPopover
              icon={
                <CustomButton
                  icon={selection.icon}
                  label={selection.name}
                  btnClass={`${styles.calendarBtn} d_btn`}
                  rightIcon={
                    <span className="ms-6 d-inline-flex">
                      <ArrowDownMedium />
                    </span>
                  }
                  clickHandler={() =>
                    setIsOpenPopover(openPopover === "range" ? null : "range")
                  }
                />
              }
              width="96px"
              PopSurfaceClass={styles.calendarPopover}
              status={openPopover === "range"}
            >
              {entityType === "portfolio" && (
                <div
                  className={styles.calendarPopoverItem}
                  onClick={() => {
                    setTabView("year");
                    setSelection({ icon: <YearIcon />, name: "Year" });
                    setCount((prev) => prev + 1);
                    setIsOpenPopover(null);
                  }}
                >
                  <YearIcon />
                  Year
                </div>
              )}

              <div
                className={styles.calendarPopoverItem}
                onClick={() => {
                  setTabView("quarter");
                  setSelection({ icon: <QuarterIcon />, name: "Quarter" });
                  setCount((prev) => prev + 1);
                  setIsOpenPopover(null);
                }}
              >
                <QuarterIcon />
                Quarter
              </div>

              <div
                className={styles.calendarPopoverItem}
                onClick={() => {
                  setTabView("month");
                  setSelection({ icon: <MonthIcon />, name: "Month" });
                  setCount((prev) => prev + 1);
                  setIsOpenPopover(null);
                }}
              >
                <MonthIcon />
                Month
              </div>
              {entityType !== "portfolio" &&
                <div
                  className={styles.calendarPopoverItem}
                  onClick={() => {
                    setSelection({ icon: <WeekIcon />, name: "Week" });
                    setCount((prev) => prev + 1);
                    setIsOpenPopover(null);
                    setTabView("week");
                  }}
                >
                  <WeekIcon />
                  Week
                </div>
              }
              {entityType !== "portfolio" &&
                <div
                  className={styles.calendarPopoverItem}
                  onClick={() => {
                    setSelection({ icon: <DayIcon />, name: "Day" });
                    setCount((prev) => prev + 1);
                    setIsOpenPopover(null);
                    setTabView("day");
                  }}
                >
                  <DayIcon />
                  Day
                </div>
              }
            </CustomPopover>
          </div>
        </div>
        <GanttChart
          isHide={isHideSideBar}
          viewType={selection.name}
          count={count}
          isFullHeight={isFullHeight}
          entityType={entityType}
          keyEvents={entityType === "portfolio" ? keyEvents : []}
        />
      </div>
    </div>
  );
};

export default connect(null, null)(Timeline);